// RepeatGuard — защита от случайного повтора ключевого действия. Если то же
// действие (тот же ключ) запускают повторно в пределах окна (10 сек), показываем
// поп-ап «Вы точно хотите повторить такую же операцию?» и ждём подтверждения.
// Повод: админ случайно продал несколько абонементов подряд.
//
// Применение: в обработчике ключевого действия обернуть запуск мутации —
//   const { guard } = useRepeatGuard();
//   guard(`sell-subscription:${clientId}:${templateId}`, () => sell.mutate(...));
// Ключ включает сущность/параметры, чтобы РАЗНЫЕ операции не считались повтором.
import { createContext, useCallback, useContext, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

const REPEAT_WINDOW_MS = 10_000;

interface RepeatGuardApi {
  /** Запустить action; при повторе того же key в пределах окна — спросить подтверждение. */
  guard: (key: string, action: () => void) => void;
}

const RepeatGuardContext = createContext<RepeatGuardApi>({
  guard: (_key, action) => action(),
});

export function RepeatGuardProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const lastByKey = useRef<Map<string, number>>(new Map());
  const [pending, setPending] = useState<{ key: string; action: () => void } | null>(null);

  const guard = useCallback((key: string, action: () => void) => {
    const now = Date.now();
    const last = lastByKey.current.get(key) ?? 0;
    if (now - last < REPEAT_WINDOW_MS) {
      // Похоже на случайный повтор — спрашиваем.
      setPending({ key, action });
      return;
    }
    lastByKey.current.set(key, now);
    action();
  }, []);

  const confirm = useCallback(() => {
    setPending((p) => {
      if (p) {
        lastByKey.current.set(p.key, Date.now());
        p.action();
      }
      return null;
    });
  }, []);

  const cancel = useCallback(() => setPending(null), []);

  return (
    <RepeatGuardContext.Provider value={{ guard }}>
      {children}
      {pending ? <RepeatConfirmDialog onConfirm={confirm} onCancel={cancel} /> : null}
    </RepeatGuardContext.Provider>
  );
}

export function useRepeatGuard(): RepeatGuardApi {
  return useContext(RepeatGuardContext);
}

function RepeatConfirmDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-card bg-canvas-white p-6 shadow-xl">
        <h2 className="text-lg font-medium tracking-tight text-ink-black">Повторить операцию?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Вы только что выполнили такое же действие. Вы точно хотите повторить эту же операцию?
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Нет
          </Button>
          <Button size="sm" onClick={onConfirm}>
            Да, повторить
          </Button>
        </div>
      </div>
    </div>
  );
}
